package com.rosario.juridico.marketplace.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.layout.*
import kotlinx.coroutines.launch
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import com.rosario.juridico.domain.PerfilAbogadoDataModel
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState
import com.rosario.juridico.navigation.Routes

private val AzulCliente = Color(0xFF1A7FD4)
private val VerdeAbogado = Color(0xFF1D9E75)

@Composable
fun OnboardingRolScreen(
    navController: NavController,
    viewModel: MarketplaceViewModel,
    role: String? = null,
    editMode: Boolean = false
) {
    val onboardingState by viewModel.onboardingState.collectAsState()
    val currentUserAbogado by viewModel.currentUserAbogado.collectAsState()
    val perfilState by viewModel.perfilAbogadoState.collectAsState()

    val isOnlyAbogado = role == "abogado"
    var isAbogadoSelected by remember(role) { mutableStateOf(isOnlyAbogado) }
    var matricula by remember(currentUserAbogado) { mutableStateOf(currentUserAbogado?.matricula ?: "") }
    var telefono by remember(currentUserAbogado) { mutableStateOf(currentUserAbogado?.telefono ?: "") }
    val coroutineScope = rememberCoroutineScope()
    var activandoCliente by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        viewModel.loadCurrentUserAbogado()
        viewModel.cargarPerfilAbogado()
    }

    LaunchedEffect(currentUserAbogado) {
        currentUserAbogado?.let { abog ->
            val abogMat = abog.matricula.orEmpty()
            val abogTel = abog.telefono.orEmpty()
            if (!editMode && abogMat.isNotBlank() && abogTel.isNotBlank()) {
                navController.navigate(Routes.LISTADO_CONSULTAS) {
                    popUpTo(Routes.INICIO) { inclusive = false }
                }
            } else {
                if (abogMat.isNotBlank() && matricula.isBlank()) matricula = abogMat
                if (abogTel.isNotBlank() && telefono.isBlank()) telefono = abogTel
            }
        }
    }

    LaunchedEffect(perfilState) {
        if (perfilState is UiState.Success) {
            val perfil = (perfilState as UiState.Success<*>).data as? PerfilAbogadoDataModel
            if (perfil != null) {
                val perfMat = perfil.matricula.orEmpty()
                val perfTel = perfil.telefono.orEmpty()
                if (!editMode && perfMat.isNotBlank() && perfTel.isNotBlank()) {
                    navController.navigate(Routes.LISTADO_CONSULTAS) {
                        popUpTo(Routes.INICIO) { inclusive = false }
                    }
                } else {
                    if (perfMat.isNotBlank() && matricula.isBlank()) matricula = perfMat
                    if (perfTel.isNotBlank() && telefono.isBlank()) telefono = perfTel
                }
            }
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .imePadding()
            .navigationBarsPadding()
    ) {
        MktHeader(
            titulo = if (isOnlyAbogado) "Perfil Profesional" else "¿Qué buscas hoy?",
            subtitulo = if (isOnlyAbogado) "Completá tus datos para acceder al Portal" else "Elige tu rol en Jurídico Rosario",
            onBack = { navController.popBackStack() }
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = if (isOnlyAbogado) "Portal de Profesionales" else "Bienvenido al Marketplace Legal",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )
            Text(
                text = if (isOnlyAbogado) "Ingresá tu matrícula y teléfono para completar tu registro y acceder a las consultas de clientes." else "Para brindarte la mejor experiencia, contanos brevemente qué necesitas hacer hoy.",
                style = MaterialTheme.typography.bodyMedium,
                textAlign = TextAlign.Center,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Spacer(modifier = Modifier.height(16.dp))

            if (!isOnlyAbogado) {
                // Tarjeta: Cliente
                var errorMessageCliente by remember { mutableStateOf<String?>(null) }
                
                if (errorMessageCliente != null) {
                    Text(
                        text = errorMessageCliente!!,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.padding(bottom = 4.dp)
                    )
                }
                
                Card(
                    onClick = { 
                        if (activandoCliente) return@Card
                        coroutineScope.launch {
                            activandoCliente = true
                            errorMessageCliente = null
                            val resultado = viewModel.actualizarRolUsuarioSync("cliente")
                            if (resultado.isSuccess) {
                                navController.popBackStack()
                            } else {
                                errorMessageCliente = resultado.exceptionOrNull()?.message ?: "Error al guardar en la nube. Revisa tu conexión."
                                activandoCliente = false
                            }
                        }
                    },
                    enabled = !activandoCliente,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = AzulCliente.copy(alpha = 0.1f)
                    ),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(20.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        if (activandoCliente) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(20.dp),
                                color = AzulCliente,
                                strokeWidth = 2.dp
                            )
                            Text(
                                text = "Activando perfil...",
                                fontWeight = FontWeight.Bold,
                                color = AzulCliente,
                                style = MaterialTheme.typography.titleMedium
                            )
                        } else {
                            Icon(Icons.Default.PersonSearch, null, tint = AzulCliente, modifier = Modifier.size(32.dp))
                            Column {
                                Text("Necesito un Abogado", fontWeight = FontWeight.Bold, color = AzulCliente, style = MaterialTheme.typography.titleMedium)
                                Text("Publicar consultas y recibir presupuestos", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            }
                        }
                    }
                }

                // Tarjeta: Abogado (para modo de selección dual)
                Card(
                    onClick = { isAbogadoSelected = !isAbogadoSelected },
                    enabled = !activandoCliente,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = if (isAbogadoSelected) VerdeAbogado.copy(alpha = 0.2f) else VerdeAbogado.copy(alpha = 0.1f)
                    ),
                    elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(20.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Icon(Icons.Default.Work, null, tint = VerdeAbogado, modifier = Modifier.size(32.dp))
                        Column {
                            Text("Soy Profesional", fontWeight = FontWeight.Bold, color = VerdeAbogado, style = MaterialTheme.typography.titleMedium)
                            Text("Captar clientes y enviar honorarios", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                }
            }

            // Formulario condicional para Abogado
            AnimatedVisibility(visible = isAbogadoSelected || isOnlyAbogado) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(top = if (isOnlyAbogado) 8.dp else 16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text("Completá tus datos profesionales", fontWeight = FontWeight.Bold)
                    OutlinedTextField(
                        value = matricula,
                        onValueChange = { matricula = it },
                        label = { Text("Matrícula Profesional") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true,
                        enabled = onboardingState !is UiState.Loading
                    )
                    OutlinedTextField(
                        value = telefono,
                        onValueChange = { telefono = it },
                        label = { Text("Teléfono del Estudio") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        enabled = onboardingState !is UiState.Loading
                    )
                    
                    val canSubmit = matricula.isNotBlank() && telefono.isNotBlank()
                    var errorMessage by remember { mutableStateOf<String?>(null) }
                    
                    if (errorMessage != null) {
                        Text(
                            text = errorMessage!!,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(bottom = 4.dp)
                        )
                    }

                    Button(
                        onClick = {
                            if (canSubmit) {
                                coroutineScope.launch {
                                    errorMessage = null
                                    val resultado = viewModel.actualizarRolUsuarioSync("abogado", matricula, telefono)
                                    if (resultado.isSuccess) {
                                        if (isOnlyAbogado) {
                                            navController.navigate(Routes.LISTADO_CONSULTAS) {
                                                popUpTo(Routes.INICIO) { inclusive = false }
                                            }
                                        } else {
                                            navController.popBackStack()
                                        }
                                    } else {
                                        errorMessage = resultado.exceptionOrNull()?.message ?: "Error al guardar en la nube. Revisa tu conexión."
                                    }
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth().height(50.dp),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = VerdeAbogado),
                        enabled = canSubmit && onboardingState !is UiState.Loading
                    ) {
                        if (onboardingState is UiState.Loading) {
                            CircularProgressIndicator(modifier = Modifier.size(24.dp), color = Color.White, strokeWidth = 2.dp)
                        } else {
                            Text("Confirmar y Entrar", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }

            if (onboardingState is UiState.Error) {
                val errorMsg = (onboardingState as UiState.Error).message
                Text(
                    text = errorMsg,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(top = 8.dp)
                )
            }
        }
    }
}
